import { v4 as uuid } from 'uuid';

import {
    OPENING_HOURS_DAYS,
    OPENING_HOURS_TASK_VALUE,
    OPENING_HOURS_WINDOWS,
} from '@/data/opening-hours';
import { TaskValue } from '@/domain/bot';
import { OpeningHours, OpeningHoursWeek } from '@/domain/contact-center';

const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;

export const isOpeningHoursTime = (value: unknown): value is string =>
    typeof value === 'string' && HH_MM.test(value);

const emptyWeek = (): OpeningHoursWeek =>
    OPENING_HOURS_DAYS.reduce((week, day) => {
        week[day] = null;
        return week;
    }, {} as OpeningHoursWeek);

export const emptyOpeningHours = (): OpeningHours =>
    OPENING_HOURS_WINDOWS.reduce((windows, name) => {
        windows[name] = emptyWeek();
        return windows;
    }, {} as OpeningHours);

/** A window that crosses midnight is rejected; `from` must be earlier than `to`. */
export const isValidOpeningHoursWindow = (
    window: unknown,
): window is [string, string] =>
    Array.isArray(window) &&
    window.length === 2 &&
    window.every(isOpeningHoursTime) &&
    window[0] < window[1];

const parseWeek = (value: unknown): OpeningHoursWeek => {
    const source = (value ?? {}) as Record<string, unknown>;

    return OPENING_HOURS_DAYS.reduce((week, day) => {
        const window = source[day];
        week[day] = isValidOpeningHoursWindow(window) ? [...window] : null;
        return week;
    }, {} as OpeningHoursWeek);
};

export const parseOpeningHours = (
    taskValues?: TaskValue[] | null,
): OpeningHours => {
    const serialized = taskValues?.find(
        (taskValue) => taskValue.variableName === OPENING_HOURS_TASK_VALUE,
    )?.value;

    if (!serialized) {
        return emptyOpeningHours();
    }

    try {
        const parsed = JSON.parse(serialized) as Record<string, unknown>;

        return OPENING_HOURS_WINDOWS.reduce((windows, name) => {
            windows[name] = parseWeek(parsed?.[name]);
            return windows;
        }, {} as OpeningHours);
    } catch {
        return emptyOpeningHours();
    }
};

export const serializeOpeningHours = (openingHours: OpeningHours): string =>
    JSON.stringify(openingHours);

/** Replaces only the opening-hours row and leaves every other task value untouched. */
export const withOpeningHours = ({
    taskValues,
    openingHours,
}: {
    taskValues?: TaskValue[] | null;
    openingHours: OpeningHours;
}): TaskValue[] => {
    const rows = taskValues ?? [];
    const value = serializeOpeningHours(openingHours);
    const existing = rows.find(
        (taskValue) => taskValue.variableName === OPENING_HOURS_TASK_VALUE,
    );

    if (!existing) {
        return [
            ...rows,
            { variableName: OPENING_HOURS_TASK_VALUE, value, id: uuid() },
        ];
    }

    return rows.map((taskValue) =>
        taskValue.id === existing.id ? { ...taskValue, value } : taskValue,
    );
};

export const isOpeningHoursTaskValue = (taskValue: TaskValue) =>
    taskValue.variableName === OPENING_HOURS_TASK_VALUE;
